import { Alert, Form, Input, Modal } from 'antd';
import { useEffect } from 'react';
import { TYRE_MOVE_TITLES, type TyreMoveFormValues, type TyreMoveKind } from '../policies/tyre-move.policy';
import { OdometerField, tyreMoveFields } from './TyreMoveFields';
import type { RotationContext } from './RotateTargetPicker';

interface TyreMoveModalProps {
  /** Null while closed. */
  kind: TyreMoveKind | null;
  subject: { id: string; serial: string } | null;
  error: string | null;
  submitting: boolean;
  positionOptions?: { value: string; label: string }[];
  /** With the position being moved from, lets a rotation be picked on the vehicle. */
  rotation?: RotationContext | null;
  onSubmit: (values: TyreMoveFormValues) => Promise<boolean>;
  onClose: () => void;
}

function moveTitle(kind: TyreMoveKind | null, subject: { serial: string } | null): string {
  if (!kind) return '';
  return subject ? `${TYRE_MOVE_TITLES[kind]} — ${subject.serial}` : TYRE_MOVE_TITLES[kind];
}

/** One modal for every move a tyre takes: the title, the move's own fields, then the odometer and a note. A refusal from the server shows inside it. */
export function TyreMoveModal({ kind, subject, error, submitting, positionOptions, rotation, onSubmit, onClose }: TyreMoveModalProps) {
  const [form] = Form.useForm<TyreMoveFormValues>();

  useEffect(() => {
    if (kind) form.resetFields();
  }, [kind, subject, form]);

  async function submit(values: TyreMoveFormValues) {
    if (await onSubmit(values)) onClose();
  }

  return (
    <Modal
      open={kind !== null}
      title={moveTitle(kind, subject)}
      okText="Save"
      confirmLoading={submitting}
      onOk={() => form.submit()}
      onCancel={onClose}
      width={rotation ? 720 : undefined}
      destroyOnHidden
    >
      {kind && (
        <>
          {error && <Alert className="mb-4" type="error" title={error} showIcon />}
          <Form form={form} layout="vertical" requiredMark={false} onFinish={submit} disabled={submitting}>
            {tyreMoveFields(kind, { subjectTyreId: subject?.id ?? '', positionOptions, rotation })}
            <OdometerField kind={kind} />
            <Form.Item name="notes" label="Notes">
              <Input.TextArea rows={2} />
            </Form.Item>
          </Form>
        </>
      )}
    </Modal>
  );
}
